"use client";

import React, { useEffect, useState } from "react";
// import Image from 'next/image';
import * as Icon from "@phosphor-icons/react/dist/ssr";
import { X } from "@phosphor-icons/react/dist/ssr";
import { useLocale, useTranslations } from "next-intl";

import FlagIcon from "../../Flag/index";
import { SITE_LOCALES } from "@/lib/constant";
import { type TLocale } from "@/lib/@types/locale";
import { defaultLocale } from "@/config";
import { Link, useRouter, usePathname } from "@/navigation";
import { LinkedinLogo, TiktokLogo } from "@phosphor-icons/react";
import { SocialList } from "@/components/Social/FollowUs";
import { contactInfo } from "@/lib/contacts";
import { message } from "antd";

interface Props {
	props: string;
	slogan?: string;
}

const TopNavOne: React.FC<Props> = ({ props, slogan }) => {
	const t = useTranslations();

	const copyTextToClipboard = async (text: string) => {
		if ("clipboard" in navigator) {
			// 使用 navigator.clipboard API 复制文本
			try {
				await navigator.clipboard.writeText(text);
				message.success(t("common.copied"));
			} catch (err) {
				console.error("Failed to copy: ", err);
			}
		} else {
			// 备用方案：旧浏览器支持
			const textarea = document.createElement("textarea");
			textarea.value = text;
			document.body.appendChild(textarea);
			textarea.select();
			try {
				document.execCommand("copy"); // 尝试复制文本
				message.success(t("common.copied"));
			} catch (err) {
				console.error("Failed to copy: ", err);
			}
			document.body.removeChild(textarea);
		}
	};

	const [isOpenLanguage, setIsOpenLanguage] = useState(false);
	const [showLanguageBanner, setShowLanguageBanner] = useState(false);
	const [browserLocale, setBrowserLocale] = useState<string | null>(null);
	const pathname = usePathname(); // next-intl 返回的 pathname 已不含 locale，如 /blog/123
	const currentSlug = pathname || "/";

	const locale = useLocale();
	const currentLocales: TLocale[] = SITE_LOCALES;

	const getFlag = (code: string) => {
		return SITE_LOCALES.find((item) => {
			return item.code === code;
		})!.flag;
	};

	// 检测浏览器语言并匹配支持的语言
	useEffect(() => {
		if (typeof window === "undefined") return;

		// 检查用户是否已经关闭过横幅
		const bannerDismissed = localStorage.getItem("languageBannerDismissed");
		if (bannerDismissed) {
			setShowLanguageBanner(false);
			return;
		}

		// 检查用户是否已经主动选择过语言
		const userHasSelectedLanguage = localStorage.getItem("userHasSelectedLanguage");
		if (userHasSelectedLanguage) {
			setShowLanguageBanner(false);
			return;
		}

		// 获取浏览器语言
		const browserLang = navigator.language || navigator.languages?.[0] || "en";

		// 提取基础语言代码（例如：zh-CN -> zh, en-US -> en）
		const baseLang = browserLang.split("-")[0].toLowerCase();

		// 尝试匹配完整语言代码（如 zh-CN, zh-Hans）
		let matchedLocale = SITE_LOCALES.find((item) => {
			const itemCode = item.code.toLowerCase();
			const browserCode = browserLang.toLowerCase();
			// 完全匹配或基础代码匹配
			return itemCode === browserCode || itemCode.startsWith(baseLang + "-") || itemCode === baseLang;
		});

		// 如果没有完全匹配，尝试基础代码匹配
		if (!matchedLocale) {
			matchedLocale = SITE_LOCALES.find((item) => {
				const itemBase = item.code.split("-")[0].toLowerCase();
				return itemBase === baseLang;
			});
		}

		// 特殊处理：zh-CN, zh-TW 等映射到 zh-Hans
		if (!matchedLocale && browserLang.toLowerCase().startsWith("zh")) {
			matchedLocale = SITE_LOCALES.find((item) => item.code === "zh-Hans");
		}

		// 如果找到匹配的语言且与当前页面语言不匹配，显示横幅
		if (matchedLocale && matchedLocale.code !== locale) {
			setBrowserLocale(matchedLocale.code);
			setShowLanguageBanner(true);
		} else {
			setShowLanguageBanner(false);
		}
	}, [locale]);

	// 关闭横幅
	const handleDismissBanner = () => {
		setShowLanguageBanner(false);
		localStorage.setItem("languageBannerDismissed", "true");
	};
	// 语言列表
	return (
		<>
			<div className={`top-nav h-[40px] ${props} `}>
				<div className="container mx-auto h-full">
					<div className="top-nav-main flex h-full justify-between">
						<div className="left-content flex items-center gap-5">
							<div className="relative">
								<div
									className="choose-type choose-language flex items-center gap-1.5"
									onClick={() => {
										setIsOpenLanguage(!isOpenLanguage);
										// setIsOpenCurrence(false);
									}}
								>
									<div className="select relative flex">
										<p className="selected caption2 flex gap-x-1 text-white">
											<FlagIcon flag={getFlag(locale)} />
											{currentLocales.find((item) => item.code === locale)?.nativeName || defaultLocale}{" "}
										</p>
										<ul className={`list-option bg-white ${isOpenLanguage ? "open" : ""} z-90`}>
											{currentLocales.map((item, index) => (
												<Link
													key={index}
													href={currentSlug}
													locale={item.code}
													className="!hover:bg-[#eeeeee]"
													onClick={() => {
														// 用户主动选择语言时，设置标记，后续不再显示横幅
														localStorage.setItem("userHasSelectedLanguage", "true");
													}}
												>
													<li className="caption2 m-1 flex items-center justify-start gap-x-1 rounded-sm border p-2">
														<FlagIcon flag={getFlag(item.code)} />
														<span className="inline-block text-nowrap">{item.nativeName}</span>
													</li>
												</Link>
											))}
										</ul>
									</div>
									<Icon.CaretDown size={12} color="#fff" />
								</div>
								{/* 语言推荐横幅 */}
								{showLanguageBanner && browserLocale && (
									<div className="fixed left-0 top-0 z-[9999] w-full bg-[#000000bb] px-4 py-3 text-left shadow-lg md:absolute md:left-full md:top-0 md:ml-1 md:flex md:w-max md:max-w-none md:items-center md:justify-start md:gap-2 md:rounded-md md:border md:border-gray-200 md:bg-[#000000bb] md:p-2.5 md:text-left">
										<span className="mr-2 inline-block align-middle md:mr-0">
											<FlagIcon flag={getFlag(browserLocale)} />
										</span>
										<span className="align-middle text-sm text-white md:text-xs">
											{t("common.languageBannerPrefix")}{" "}
											{currentLocales.find((item) => item.code === browserLocale)?.nativeName}{" "}
											{t("common.languageBannerSuffix")}
										</span>
										<span className="inline-block w-2 md:hidden"></span>
										<Link
											href={currentSlug}
											locale={browserLocale}
											className="align-middle font-medium text-blue-400 underline hover:text-blue-500"
											onClick={() => {
												// 用户通过横幅选择语言时，也设置标记，后续不再显示横幅
												localStorage.setItem("userHasSelectedLanguage", "true");
											}}
										>
											{t("common.yes")}
										</Link>
										<span className="mx-1 align-middle text-sm text-white md:text-xs">{t("common.or")}</span>
										<button
											onClick={handleDismissBanner}
											className="align-middle text-blue-400 underline transition-colors hover:text-blue-500"
											aria-label={t("common.closeBanner")}
											type="button"
										>
											{t("common.no")}
										</button>
									</div>
								)}
							</div>
						</div>
						<div className="flex items-center text-center text-white max-md:hidden">
							<span className="text-xs"> {t("home.slogan")}</span>

							<a
								href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, "")}`}
								target="_blank"
								rel="noopener noreferrer"
								className="mx-3 text-white"
							>
								WhatsAPP：{contactInfo.whatsapp}
							</a>
							<a href={`mailto:${contactInfo.email}`} className="text-white underline hover:opacity-90">
								{contactInfo.email}
							</a>
						</div>
						{/* <div
							className="trustpilot-widget my-auto max-md:hidden"
							data-locale="en-GB"
							data-template-id="5419b637fa0340045cd0c936"
							data-businessunit-id="68808142dd0a4c4d3e411b9c"
							data-style-height="24px"
							data-style-width="100%"
							data-token="641f8116-607b-46db-8dbe-580350245132"
						>
							<a href="https://uk.trustpilot.com/review/www.cncprotolabs.com" target="_blank" rel="noopener">
								Trustpilot
							</a>
						</div> */}
						<div className="right-content flex items-center gap-5 max-md:ml-5">
							<SocialList></SocialList>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default TopNavOne;
